import { Content } from '@radix-ui/react-tabs';
import { type FormApi, useForm } from '@tanstack/react-form';
import { Block, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { ButtonSeeHowItWorks } from '../../../components/button-see-how-it-works';
import { Button } from '../../../components/buttons/button';
import { ErrorField } from '../../../components/inputs/error-field';
import { Tabs, TabsHeader } from '../../../components/tabs';
import { ToastVariant } from '../../../components/toast';
import {
  type Brand,
  type BrandCreate,
  BrandProductCategory,
  BrandTypeOfBusiness,
  BrandVoice,
} from '../../../entity/api/brand';
import { addTrackCrumb, trackError } from '../../../error/tracking';
import { type ErrorAny, mapErrorToTranslation } from '../../../error/utils';
import { captureException } from '../../../logger/capture-exception';
import { useBrandById, useUpdateBrand } from '../../../services/storage/brands-queries';
import { tNamespace } from '../../../services/translation';
import { setSelectedBrand } from '../../../shared/brand';
import { useToast } from '../../../shared/contexts/toast-context';
import { CompanyTab } from './-tab-company';
import { ContentTab } from './-tab-content';
import { ExcerptTab } from './-tab-excerpt';
import { KeywordBlacklistedTab } from './-tab-keyword-blacklisted';
import { PersonaTab } from './-tab-persona';
import { BrandFillerBanner } from './component/-brand-filler-banner';
import { BrandFillerModal } from './component/-brand-filler-modal';
import { ModalDiscardChanges } from './component/-modal-discard-changes';
import { ModalUnsavedChanges } from './component/-modal-unsaved-changes';

const t = tNamespace('app.brand-context.index');

// NOTE: this is bound to change and be controlled at higher level.
export enum BrandFillerBannerStatus {
  NotGenerated = 'notGenerated',
  ReviewGenerated = 'reviewGenerated',
  AlreadyGenerated = 'alreadyGenerated',
}

export const Route = createFileRoute('/app/_sidebar-layout/brand-context/')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/_sidebar-layout/brand-context/$brandId',
      level: 'info',
    });
    // NOTE: brand are queried on every routes, there is no need to query it again.
  },
  validateSearch: function (search: Record<string, unknown>): {
    brandId: string;
  } {
    const brandId = search.brandId as string | undefined | null;
    if (brandId == null) {
      return { brandId: '' };
    }

    setSelectedBrand(brandId).catch(function (err) {
      captureException(
        err,
        {
          file: 'app/_sidebar-layout.brand-context/index.tsx',
          method: 'validateSearch',
          infoMessage: 'unable to set selected brand from search',
        },
        trackError
      );
    });

    return {
      brandId,
    };
  },
  // eslint-disable-next-line no-use-before-define
  component: Index,
});

function timeout(ms: number) {
  return new Promise(function (resolve) {
    setTimeout(resolve, ms);
  });
}

function getFormValues(form: FormApi<BrandCreate>) {
  const currentFormValues = {
    name: form.getFieldValue('name'),
    organizationId: form.getFieldValue('organizationId'),
    isDefault: form.getFieldValue('isDefault'),
    companyName: form.getFieldValue('companyName'),
    companyWebsite: form.getFieldValue('companyWebsite'),
    productCategory: form.getFieldValue('productCategory'),
    productType: form.getFieldValue('productType'),
    typeOfBusiness: form.getFieldValue('typeOfBusiness'),
    activity: form.getFieldValue('activity'),
    country: form.getFieldValue('country'),
    language: form.getFieldValue('language'),
    persona: form.getFieldValue('persona'),
    excerpt: form.getFieldValue('excerpt'),
    extra: form.getFieldValue('extra'),
    contentVoice: form.getFieldValue('contentVoice'),
    contentVoiceTone: form.getFieldValue('contentVoiceTone'),
    contentInclusiveLanguage: form.getFieldValue('contentInclusiveLanguage'),
    contentTalkAboutBrand: form.getFieldValue('contentTalkAboutBrand'),
    contentHasEmoji: form.getFieldValue('contentHasEmoji'),
    productVoice: form.getFieldValue('productVoice'),
    productVoiceTone: form.getFieldValue('productVoiceTone'),
    productInclusiveLanguage: form.getFieldValue('productInclusiveLanguage'),
    productTalkAboutBrand: form.getFieldValue('productTalkAboutBrand'),
    collectionVoice: form.getFieldValue('collectionVoice'),
    collectionVoiceTone: form.getFieldValue('collectionVoiceTone'),
    collectionInclusiveLanguage: form.getFieldValue('collectionInclusiveLanguage'),
    collectionTalkAboutBrand: form.getFieldValue('collectionTalkAboutBrand'),
    blacklist: form.getFieldValue('blacklist'),
  };

  return currentFormValues;
}

function updateFormWithBrandValues(form: FormApi<BrandCreate>, values: BrandCreate) {
  form.setFieldValue('name', values.name);
  form.setFieldValue('organizationId', values.organizationId);
  form.setFieldValue('isDefault', values.isDefault);
  form.setFieldValue('companyName', values.companyName);
  form.setFieldValue('companyWebsite', values.companyWebsite);
  form.setFieldValue('productCategory', values.productCategory);
  form.setFieldValue('productType', values.productType);
  form.setFieldValue('typeOfBusiness', values.typeOfBusiness);
  form.setFieldValue('activity', values.activity);
  form.setFieldValue('country', values.country);
  form.setFieldValue('language', values.language);
  form.setFieldValue('persona', values.persona);
  form.setFieldValue('excerpt', values.excerpt);
  form.setFieldValue('extra', values.extra);
  form.setFieldValue('contentVoice', values.contentVoice);
  form.setFieldValue('contentVoiceTone', values.contentVoiceTone);
  form.setFieldValue('contentInclusiveLanguage', values.contentInclusiveLanguage);
  form.setFieldValue('contentTalkAboutBrand', values.contentTalkAboutBrand);
  form.setFieldValue('contentHasEmoji', values.contentHasEmoji);
  form.setFieldValue('productVoice', values.productVoice);
  form.setFieldValue('productVoiceTone', values.productVoiceTone);
  form.setFieldValue('productInclusiveLanguage', values.productInclusiveLanguage);
  form.setFieldValue('productTalkAboutBrand', values.productTalkAboutBrand);
  form.setFieldValue('collectionVoice', values.collectionVoice);
  form.setFieldValue('collectionVoiceTone', values.collectionVoiceTone);
  form.setFieldValue('collectionInclusiveLanguage', values.collectionInclusiveLanguage);
  form.setFieldValue('collectionTalkAboutBrand', values.collectionTalkAboutBrand);
  form.setFieldValue('blacklist', values.blacklist);
}

function getBrandDefaults(brand?: Brand) {
  return {
    name: brand?.name ?? '',
    organizationId: brand?.organizationId ?? '',
    isDefault: brand?.isDefault ?? false,
    companyName: brand?.companyName ?? '',
    companyWebsite: brand?.companyWebsite ?? '',
    productCategory: brand?.productCategory ?? BrandProductCategory.NA,
    productType: brand?.productType ?? '',
    typeOfBusiness: brand?.typeOfBusiness ?? BrandTypeOfBusiness.NA,
    activity: brand?.activity ?? '',
    country: brand?.country ?? '',
    language: brand?.language ?? '',
    persona: brand?.persona ?? [],
    excerpt: brand?.excerpt ?? [],
    extra: brand?.extra ?? '',

    contentVoice: brand?.contentVoice ?? BrandVoice.FirstPersonSingular,
    contentVoiceTone: [],
    contentInclusiveLanguage: brand?.contentInclusiveLanguage ?? false,
    contentTalkAboutBrand: brand?.contentTalkAboutBrand ?? false,
    contentHasEmoji: brand?.contentHasEmoji ?? false,
    productVoice: brand?.productVoice ?? BrandVoice.FirstPersonSingular,
    productVoiceTone: brand?.productVoiceTone ?? [],
    productInclusiveLanguage: brand?.productInclusiveLanguage ?? false,
    productTalkAboutBrand: brand?.productTalkAboutBrand ?? false,
    collectionVoice: brand?.collectionVoice ?? BrandVoice.FirstPersonSingular,
    collectionVoiceTone: brand?.collectionVoiceTone ?? [],
    collectionInclusiveLanguage: brand?.collectionInclusiveLanguage ?? false,
    collectionTalkAboutBrand: brand?.collectionTalkAboutBrand ?? false,
    blacklist: brand?.blacklist ?? [],
  };
}

function Index() {
  const { brandId }: { brandId: string } = Route.useSearch();
  const [brand, setBrand] = useState<Brand | undefined>(undefined);
  const [errors, setErrors] = useState<string>();
  const [isBrandFillerModalOpen, setIsBrandFillerModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bannerStatus, setBannerStatus] = useState(BrandFillerBannerStatus.NotGenerated);
  const [_savedFormState, setSavedFormState] = useState<BrandCreate | undefined>(undefined);
  const [_generatedFormState, setGeneratedFormState] = useState<BrandCreate | undefined>(undefined);
  const [isModalDiscardChangesOpen, setIsModalDiscardChangesOpen] = useState(false);
  const addToast = useToast();

  // TanStack Query hooks
  const { data: brandData, isLoading: isBrandLoading } = useBrandById(brandId);
  const updateBrandMutation = useUpdateBrand();

  async function saveBrand(brandId: string, newBrand: Partial<Brand>): Promise<Brand | undefined> {
    setErrors(undefined);
    setLoading(true);
    if (brandId.length === 0) {
      setErrors(t('brandNotFound'));
      setLoading(false);
      return undefined;
    }

    // NOTE: when a user is updating the brand for the first time, we need to set the isDefault to false
    // The user is now aware of the brand context and it's not the default brand anymore
    if (newBrand.isDefault) {
      newBrand.isDefault = false;
    }

    // TODO: why do we need a timeout?
    await timeout(500);

    return updateBrandMutation
      .mutateAsync({ id: brandId, data: newBrand })
      .then(function (updatedBrand) {
        addToast({
          title: t('brandUpdated'),
          variant: ToastVariant.Success,
          id: Date.now(),
        });

        setLoading(false);
        return updatedBrand;
      })
      .catch(function (err: ErrorAny) {
        const errMsg = mapErrorToTranslation(err);
        setErrors(errMsg);
        captureException(
          err,
          {
            file: 'app/_sidebar-layout.brand-context/index.tsx',
            method: 'onSubmit',
            infoMessage: 'unable to update brand',
          },
          trackError
        );

        setLoading(false);
        return undefined;
      });
  }

  const form = useForm<BrandCreate>({
    defaultValues: getBrandDefaults(brandData),

    onSubmit: async function (values) {
      // NOTE: we don't want the data to be updated with the new incoming values
      //       in case we are changing from the unsaved changes modal
      await saveBrand(brand?._id ?? '', { ...values.value }).then(function (updatedBrand) {
        // Update local brand state and reset form with fresh data
        setBrand(updatedBrand);
        form.reset(getBrandDefaults(updatedBrand));
      });
    },
  });

  function handleBrandGenerated(generatedBrand: Brand) {
    // Save current form state (pre-generated) to allow discarding
    const currentFormValues = getFormValues(form);

    setSavedFormState(currentFormValues);

    // Prepare generated values
    const generatedValues = {
      ...getBrandDefaults(generatedBrand),
      organizationId: brand?.organizationId ?? '',
    };

    // Store the generated values to compare against for "isNew" detection
    setGeneratedFormState(generatedValues);

    // Update form with generated brand data
    updateFormWithBrandValues(form, generatedValues);

    // Banner status update
    setBannerStatus(BrandFillerBannerStatus.ReviewGenerated);
  }

  function handleDiscardChanges() {
    if (_savedFormState != null) {
      if (brandData?.excerpt?.length != null && brandData?.excerpt?.length > 0) {
        setBannerStatus(BrandFillerBannerStatus.AlreadyGenerated);
      } else {
        setBannerStatus(BrandFillerBannerStatus.NotGenerated);
      }
      updateFormWithBrandValues(form, _savedFormState);
      setGeneratedFormState(undefined); // Clear generated state
    }
  }

  useEffect(
    function () {
      if (brandId == null || brandId.length === 0) {
        setBrand(undefined);
        return;
      }

      // at this point, we require a brand
      // Only redirect when loading is complete and data is truly missing
      if (!isBrandLoading && brandData == null) {
        window.location.href = '/app';
        return;
      }

      // Still loading, wait for data
      if (isBrandLoading) {
        return;
      }

      // if same brand, we don't want to update it (check against brandData to avoid stale closure)
      if (brandData?._id === brandId && brand?._id === brandId) {
        return;
      }

      setBrand(brandData);
      if (brandData?.excerpt?.length != null && brandData?.excerpt?.length > 0) {
        setBannerStatus(BrandFillerBannerStatus.AlreadyGenerated);
      } else {
        setBannerStatus(BrandFillerBannerStatus.NotGenerated);
      }
      form.reset(getBrandDefaults(brandData));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brandData, brandId, isBrandLoading]
  );

  return (
    <Block shouldBlockFn={() => form.state.isDirty} withResolver>
      {function ({ status, proceed, reset }) {
        return (
          <div className="h-full w-full flex-1 overflow-y-auto">
            <div className="mx-auto flex h-full w-full max-w-content flex-col p-4 md:p-0">
              <div className="flex items-center justify-between pt-6 md:pt-12">
                <div className="flex flex-col gap-1">
                  <p className="font-display-2">{t('title')}</p>
                  <p className="font-body-2-r text-branding-gray">{t('description')}</p>
                  <ButtonSeeHowItWorks videoUrl="https://www.youtube.com/embed/nYXkxEqUmm8" />
                </div>
                {bannerStatus !== BrandFillerBannerStatus.ReviewGenerated ? (
                  <Button
                    size="md"
                    variant="accent"
                    loading={loading}
                    onClick={function () {
                      if (!form.state.isFieldsValid) {
                        return addToast({
                          title: t('brandInvalid'),
                          description: t('brandInvalidDescription'),
                          variant: ToastVariant.Error,
                          id: Date.now(),
                        });
                      }
                      void form.handleSubmit();
                    }}
                  >
                    {t('saveChanges')}
                  </Button>
                ) : null}
              </div>
              <BrandFillerBanner
                className="mt-6"
                status={bannerStatus}
                openModalGeneration={() => setIsBrandFillerModalOpen(true)}
                loadingSave={loading}
                openModalDiscard={function () {
                  setIsModalDiscardChangesOpen(true);
                }}
                saveBrandGenerated={function () {
                  void form.handleSubmit().then(function () {
                    setBannerStatus(BrandFillerBannerStatus.AlreadyGenerated);
                    setGeneratedFormState(undefined);
                    setSavedFormState(undefined);
                  });
                }}
              />
              <div className="flex-1 pb-20">
                <Tabs
                  defaultValue="1"
                  header={
                    <div className="pb-8 pt-6">
                      <TabsHeader
                        headers={[
                          { value: '1', title: t('general') },
                          { value: '2', title: t('personas') },
                          { value: '3', title: t('writingStyle') },
                          { value: '4', title: t('excerpts') },
                          { value: '5', title: t('keywordsBlacklisted') },
                        ]}
                        className="shrink-0 bg-white"
                        innerClassNames="px-2 shrink-0"
                      />
                    </div>
                  }
                  content={
                    <>
                      <ErrorField sizeField="lg" error={errors} />
                      <form className="h-full">
                        <Content value="1">
                          <CompanyTab form={form} generatedFormState={_generatedFormState} />
                        </Content>
                        <Content value="2">
                          <PersonaTab form={form} generatedFormState={_generatedFormState} />
                        </Content>
                        <Content value="3">
                          <ContentTab form={form} generatedFormState={_generatedFormState} />
                        </Content>
                        <Content value="4">
                          <ExcerptTab form={form} generatedFormState={_generatedFormState} />
                        </Content>
                        <Content value="5">
                          <KeywordBlacklistedTab
                            form={form}
                            generatedFormState={_generatedFormState}
                          />
                        </Content>
                      </form>
                    </>
                  }
                />
              </div>
            </div>
            {status === 'blocked' && (
              <ModalUnsavedChanges
                isOpen={true}
                onClose={function () {
                  reset();
                }}
                onSave={function () {
                  const brandId = brand?._id ?? '';

                  // on this case, we want to collect the data before it changes
                  // when the new one enters on the defaults to the form
                  // as such, we want to save the brand directly instead of using
                  // the form submit
                  form
                    .validateAllFields('submit')
                    .then(async function (validation) {
                      setGeneratedFormState(undefined);
                      setSavedFormState(undefined);

                      // we want to reset if there was any error and not save
                      if (validation.length > 0) {
                        reset();
                        return;
                      }

                      await saveBrand(brandId, getFormValues(form));
                      return proceed();
                    })
                    .catch(function (err) {
                      captureException(
                        err,
                        {
                          file: 'app/_sidebar-layout.brand-context/index.tsx',
                          method: 'ModalUnsavedChanges.onSave',
                          infoMessage: 'unable to update brand',
                        },
                        trackError
                      );
                    });
                }}
                forceExit={function () {
                  setGeneratedFormState(undefined);
                  setSavedFormState(undefined);
                  proceed();
                }}
              />
            )}
            <BrandFillerModal
              open={isBrandFillerModalOpen}
              onClose={() => setIsBrandFillerModalOpen(false)}
              currentBrand={brand ?? undefined}
              onBrandGenerated={handleBrandGenerated}
            />
            <ModalDiscardChanges
              isOpen={isModalDiscardChangesOpen}
              onClose={() => setIsModalDiscardChangesOpen(false)}
              onDiscard={function () {
                handleDiscardChanges();
                setIsModalDiscardChangesOpen(false);
              }}
            />
          </div>
        );
      }}
    </Block>
  );
}
